import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from 'recharts';

// Parity with prototype component API
// data: [{ subject: string, A: number (1..6), fullMark: number (6) }]
// Reduce default diagram size by ~15%
const RadialChart = ({ title = 'Value Maturity Index', data = [], className = '', height = 208, showChips = true, empty = false }) => {
  const intl = useIntl();

  const DEFINITIONS = {
    Identify: {
      definition: intl.formatMessage({ id: 'succession.value_acceleration.radial_chart.identify_definition' }),
      why: intl.formatMessage({ id: 'succession.value_acceleration.radial_chart.identify_why' }),
    },
    Protect: {
      definition: intl.formatMessage({ id: 'succession.value_acceleration.radial_chart.protect_definition' }),
      why: intl.formatMessage({ id: 'succession.value_acceleration.radial_chart.protect_why' }),
    },
    Build: {
      definition: intl.formatMessage({ id: 'succession.value_acceleration.radial_chart.build_definition' }),
      why: intl.formatMessage({ id: 'succession.value_acceleration.radial_chart.build_why' }),
    },
    Harvest: {
      definition: intl.formatMessage({ id: 'succession.value_acceleration.radial_chart.harvest_definition' }),
      why: intl.formatMessage({ id: 'succession.value_acceleration.radial_chart.harvest_why' }),
    },
    Manage: {
      definition: intl.formatMessage({ id: 'succession.value_acceleration.radial_chart.manage_definition' }),
      why: intl.formatMessage({ id: 'succession.value_acceleration.radial_chart.manage_why' }),
    },
  };

  const yourScoreLabel = intl.formatMessage({ id: 'succession.value_acceleration.radial_chart.your_score' });
  const definitionLabel = intl.formatMessage({ id: 'succession.value_acceleration.radial_chart.definition' });
  const whyItMattersLabel = intl.formatMessage({ id: 'succession.value_acceleration.radial_chart.why_it_matters' });

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    const p = payload[0];
    const subject = label || p.payload?.subject;
    const score = Number(p.value);
    const max = Number(p.payload?.fullMark) || 6;
    const meta = DEFINITIONS[subject] || {};
    return (
      <div className="rounded-lg shadow-succession-lg border border-succession-border bg-succession-bg-primary p-3 max-w-xs">
        <div className="text-sm font-semibold text-succession-text-primary mb-1">{subject}</div>
        <div className="text-xs text-succession-text-secondary mb-2">
          <span className="font-medium text-succession-text-primary">{yourScoreLabel}</span>
          {isFinite(score) ? `${score}/${max}` : `—/${max}`}
        </div>
        {meta.definition && (
          <div className="text-xs text-succession-text-secondary mb-1"><span className="font-medium text-succession-text-primary">{definitionLabel}</span>{meta.definition}</div>
        )}
        {meta.why && (
          <div className="text-xs text-succession-text-secondary"><span className="font-medium text-succession-text-primary">{whyItMattersLabel}</span>{meta.why}</div>
        )}
      </div>
    );
  };

  const safeData = useMemo(() => {
    return Array.isArray(data) && data.length > 0
      ? data
      : [
          { subject: 'Identify', A: 3, fullMark: 6 },
          { subject: 'Protect', A: 3, fullMark: 6 },
          { subject: 'Build', A: 3, fullMark: 6 },
          { subject: 'Harvest', A: 3, fullMark: 6 },
          { subject: 'Manage', A: 3, fullMark: 6 },
        ];
  }, [data]);

  const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const [isVisible, setIsVisible] = useState(prefersReducedMotion);
  const [animatedData, setAnimatedData] = useState(() => safeData.map(item => ({ ...item, A: prefersReducedMotion ? item.A : 0 })));

  useEffect(() => {
    let entranceTimer;
    let dataTimer;
    if (prefersReducedMotion) {
      setIsVisible(true);
      setAnimatedData(safeData);
      return;
    }
    entranceTimer = setTimeout(() => {
      setIsVisible(true);
    }, 200);
    dataTimer = setTimeout(() => {
      setAnimatedData(safeData);
    }, 600);
    return () => {
      if (entranceTimer) clearTimeout(entranceTimer);
      if (dataTimer) clearTimeout(dataTimer);
    };
  }, [safeData, prefersReducedMotion]);

  return (
    <div data-radialchart-root="1" className={`w-full transform transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-95'} ${className}`}>
      <style>{`
        @keyframes vmiPulse { 0% { fill-opacity: 0.25; } 50% { fill-opacity: 0.4; } 100% { fill-opacity: 0.25; } }
        .recharts-radar.vmi-pulse { animation: vmiPulse 2.4s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .recharts-radar.vmi-pulse { animation: none !important; } }
      `}</style>
      <h3 className="m-0 p-0 text-succession-foreground mb-2 transition-all duration-500">
        {title}
      </h3>
      {/* Stacked chips on the left, vertically centered relative to the chart */}
      <div className={showChips ? "relative" : "relative"}>
        {showChips && (
          <>
            <div className="absolute left-0 top-1/2 -translate-y-1/2 flex flex-col gap-2 items-start">
              {safeData.map((d) => {
                const meta = DEFINITIONS[d.subject] || {};
                const label = d.subject;
                const score = Number(d.A);
                const max = Number(d.fullMark) || 6;
                const chipTitle = `${label}: ${meta.definition || ''}${meta.why ? ' — Why: ' + meta.why : ''}`;
                return (
                  <button
                    key={`vmi-chip-${label}`}
                    type="button"
                    title={chipTitle}
                    className="px-3 py-1 rounded-full border border-succession-border bg-succession-bg-primary text-succession-text-primary text-xs hover:bg-succession-primary-10 shadow-sm"
                    onClick={(e) => {
                      try {
                        const root = e.currentTarget.closest('[data-radialchart-root]');
                        const panel = root ? root.querySelector('[data-slot="vmi-detail"]') : null;
                        if (panel && panel.dataset && panel.dataset.slot === 'vmi-detail') {
                          panel.innerHTML = `
                            <div class=\"rounded-lg border border-succession-border bg-succession-bg-primary p-3 shadow-succession-md\">
                              <div class=\"text-sm font-semibold text-succession-text-primary mb-1\">${label}</div>
                              <div class=\"text-xs text-succession-text-secondary mb-2\"><span class=\"font-medium text-succession-text-primary\">${yourScoreLabel}</span>${isFinite(score) ? `${score}/${max}` : `—/${max}`}</div>
                              ${meta.definition ? `<div class=\\"text-xs text-succession-text-secondary mb-1\\"><span class=\\"font-medium text-succession-text-primary\\">${definitionLabel}</span>${meta.definition}</div>` : ''}
                              ${meta.why ? `<div class=\\"text-xs text-succession-text-secondary\\"><span class=\\"font-medium text-succession-text-primary\\">${whyItMattersLabel}</span>${meta.why}</div>` : ''}
                            </div>`;
                        }
                      } catch (_) {}
                    }}
                  >
                    {label}: {isFinite(score) ? `${score}/${max}` : `—/${max}`}
                  </button>
                );
              })}
            </div>
            {/* Detail panel rendered to the right of the stacked chips; may overlap chart slightly */}
            <div className="absolute left-36 top-1/2 -translate-y-1/2 max-w-sm" data-slot="vmi-detail"></div>
          </>
        )}
        <div className={showChips ? "pl-28" : "pl-0"} style={{ height: Math.min(height, 380), maxHeight: 380 }}>
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={animatedData} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
              <PolarGrid stroke="#e5e7eb" className={`transition-opacity duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`} />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 12, fill: '#6b7280' }} className={`transition-opacity duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`} />
              <PolarRadiusAxis angle={90} domain={[1, 6]} tick={{ fontSize: 10, fill: '#9ca3af' }} tickCount={6} className={`transition-opacity duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`} />
              <Tooltip content={<CustomTooltip />} cursor={false} wrapperStyle={{ outline: 'none' }} />
              {!empty && (
                <Radar
                  name="Score"
                  dataKey="A"
                  stroke="var(--succession-primary)"
                  fill="var(--succession-primary)"
                  fillOpacity={0.3}
                  strokeWidth={2}
                  className={isVisible && !prefersReducedMotion ? 'vmi-pulse' : ''}
                  isAnimationActive={!prefersReducedMotion}
                  animationBegin={800}
                  animationDuration={1200}
                  animationEasing="ease-out"
                />
              )}
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default RadialChart;


